// src/client/renderer/character/types.ts
// ColorId, HeadId, EyeId, CapeId, CharacterCustomization, COLOR_ID_TO_HEX live in shared so server
// can use them without any browser/THREE dependency. Re-exported here so client imports are unchanged.
import type { HeadId, EyeId, CapeId } from '@shared/game/types';
export type { ColorId, HeadId, EyeId, CapeId, CharacterCustomization } from '@shared/game/types';
export { COLOR_ID_TO_HEX } from '@shared/game/types';

import type * as THREE from 'three';
import type { CharacterAnimationDebugState } from './animation-debug';

export type CharacterSocketName = 'head' | 'face' | 'cape' | 'leftHand' | 'rightHand';
export type CharacterAnimationAlias =
  | 'idle'
  | 'walk'
  | 'idleTurnLeft'
  | 'idleTurnRight'
  | 'walkTurnLeft'
  | 'walkTurnRight'
  | 'punch'
  | 'knockdown'
  | 'standUp'
  | 'defeat'
  | 'victory'
  | 'diceStretch'
  | 'diceCatch'
  | 'drink'
  | 'emoteGG'
  | 'emoteOof'
  | 'emoteTaunt'
  | 'emoteThanks'
  | 'emoteCry'
  | 'emoteCheer';
export type EyeExpression =
  | 'base'
  | 'happy'
  | 'sad'
  | 'angry'
  | 'surprised'
  | 'confused'
  | 'excited'
  | 'dead';

export interface CharacterCapeSimulation {
  update(dt: number): void;
  reset(): void;
  dispose(): void;
}

/** Output of character assembler — opaque handle owning the rig. */
export interface CharacterRig {
  root: THREE.Group; // Add to scene via `scene.add(rig.root)`.
  visualRoot?: THREE.Group; // Visual-only child; gameplay placement stays on root.
  modelRoot?: THREE.Object3D; // Cloned GLB root or static body root under visualRoot.
  bodyMesh: THREE.Mesh; // Legacy bridge for older animation/UI helpers.
  bodySkinnedMesh?: THREE.SkinnedMesh | null;
  renderMeshes?: THREE.Mesh[];
  colorableMeshes?: THREE.Mesh[];
  mixer?: THREE.AnimationMixer | null;
  actions?: Map<string, THREE.AnimationAction>;
  tick?(dt: number): void;
  playAnimation?(
    alias: CharacterAnimationAlias | string,
    options?: { readonly timeScale?: number },
  ): boolean;
  setEyeExpression?(expression: EyeExpression): void;
  getAnimationDebugState?(): CharacterAnimationDebugState;
  resetSimulation?(): void;
  resolveSocket?(name: CharacterSocketName | string): THREE.Object3D | null;
  dispose(): void; // Disposes GPU resources, detaches root.
  /** Internal slot — set by swapHead, disposed via disposeHead. */
  _headRoot?: THREE.Object3D | null;
  /** Internal slot — set by swapHead, disposed via disposeHead (called from rig.dispose).
   *  Underscore prefix signals do-not-mutate externally. */
  _headMesh?: THREE.Mesh | null;
  /** Legacy eye sprite slot retained for old fixtures; new runtime uses _eyeGroup. */
  _eyeSprite?: THREE.Sprite | null;
  /** Internal slot — set by attachEyeMeshes, disposed via disposeEyeMeshes. */
  _eyeGroup?: THREE.Group | null;
  /** Monotonic guard for async eye expression texture loads. */
  _eyeGeneration?: number;
  /** Currently requested/rendered cosmetic expression. */
  _eyeExpression?: EyeExpression;
  /** Internal slot — set by attachCape, disposed via disposeCape (called from rig.dispose).
   *  Null when capeId === 'none' (sentinel — butt visible per spec §9 line 669).
   *  Underscore prefix signals do-not-mutate externally. */
  _capeMesh?: THREE.Mesh | null;
  _capeSimulation?: CharacterCapeSimulation | null;
}

/** Mapping HeadId → manifest asset id (verified against assets/manifest.json by Task 0.1). */
export const HEAD_ID_TO_ASSET_ID: Record<HeadId, string> = {
  horse: 'head-horse',
  chicken: 'head-chicken',
  'cardboard-box': 'head-cardboard-box',
  shark: 'head-shark',
  pumpkin: 'head-pumpkin',
  alien: 'head-alien',
  unicorn: 'head-unicorn',
  'traffic-cone': 'head-traffic-cone',
};

/** Map EyeId → manifest asset id. Each id resolves to a per-eye texture
 *  at assets/textures/eyes/<id>/base.png (verified against manifest at plan-authoring).
 *  NOTE: 'x-eyes' maps to 'eye-sheet-xeyes' (directory uses 'xeyes', not 'x-eyes'). */
export const EYE_ID_TO_ASSET_ID: Record<EyeId, string> = {
  dots: 'eye-sheet-dots',
  angry: 'eye-sheet-angry',
  sleepy: 'eye-sheet-sleepy',
  crazy: 'eye-sheet-crazy',
  hearts: 'eye-sheet-hearts',
  'x-eyes': 'eye-sheet-xeyes',
  cyclops: 'eye-sheet-cyclops',
  stars: 'eye-sheet-stars',
};

/** Map CapeId → manifest asset id (verified against assets/manifest.json,
 *  category `cape_texture`). The 'none' sentinel is intentionally excluded
 *  via Exclude<CapeId, 'none'> — there is no texture asset for the no-cape state. */
export const CAPE_ID_TO_ASSET_ID: Record<Exclude<CapeId, 'none'>, string> = {
  starter: 'cape-texture-starter',
  fire: 'cape-texture-fire',
  rainbow: 'cape-texture-rainbow',
  cosmic: 'cape-texture-cosmic',
  golden: 'cape-texture-golden',
  wireframe: 'cape-texture-wireframe',
  water: 'cape-texture-water',
  lava: 'cape-texture-lava',
};
